import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { apiErrorMessage, createDemoPersonaState, noteTenantUnavailability } from '@lumi/app-kit';
import type { DemoJourneys, DemoOffer, DemoOfferModule, DemoRebuild, DemoStage } from '@lumi/app-kit';
import { userApi } from '../utils/api';
import { useAuthStore } from './auth';
import { useTenantsStore } from './tenants';

// Suite-level onboarding (ADR 0030; staged commit ADR 0032). `unset` = the
// first-run fork is unresolved; `demo_seeding` = the coherent-cast seed is in
// flight; `wiping` = Finish's wipe-if-demo is running; `applying` = the client is
// replaying the staged wizard writes into the now-empty modules.
export type OnboardingMode = 'unset' | 'setup' | 'demo_seeding' | 'demo' | 'wiping' | 'applying';

export type SectionStatus = 'complete' | 'skipped' | 'in_progress';

export type OnboardingSection = { status: SectionStatus; updatedAt: string | null };

// Module keys as returned by user-api (master `tenant_module` rows).
export type ModuleKey = 'ats' | 'time_tracking' | 'apprenticeship' | 'people';

export type OnboardingStatus = {
  mode: OnboardingMode;
  firstRunResolved: boolean;
  isAdmin: boolean;
  // Checkout-captured company name (master tenants.name); the wizard shows it
  // read-only and never asks for it (ADR 0032).
  companyName: string | null;
  demo: {
    active: boolean;
    seeding: boolean;
    // The caller is a guest of the demo (issue 669): a member who is neither the
    // owner nor an admin. About the caller only.
    guest?: boolean;
    // Finish's wipe-if-demo is running; the wizard shows the commit screen until
    // wipe-complete flips the mode to 'applying'.
    wiping: boolean;
    // The client is replaying staged writes into the now-empty modules (ADR 0032).
    applying: boolean;
    // seeding || wiping || applying — the suite is being prepared / committed.
    preparing: boolean;
    // Server verdict: the in-flight seed/wipe/replay exceeded the staleness window
    // (lost worker/message / abandoned replay) and a recovery action is accepted.
    stalled: boolean;
    seededAt: string | null;
    requestId: string | null;
    error: { reason: string } | null;
    // The persona sheet; present only while the demo is active (#409).
    stage?: DemoStage | null;
    // The module set the demo was entered or last reset with, while active or
    // seeding (ADR 0083).
    modules?: string[] | null;
    // The rebuild this seed is, if it is one — a reset (#409 M2).
    rebuild?: DemoRebuild | null;
  };
  sections: Record<string, OnboardingSection>;
  // Staged wizard answers + per-section replay markers (ADR 0032).
  staged: Record<string, any>;
  applied: Record<string, string>;
  checkedOutModules: string[];
  // What the demo's module picker offers (ADR 0083): every demo module in seed
  // order, `locked` when it is always in (People, or booked).
  demoOffer?: DemoOffer | null;
  // AVV acceptance (issue 332): gates `choose-setup` and `commit/begin` (guided
  // and empty, including a demo-abandon), never `demo/enter`. Recorded
  // server-side by `POST .../onboarding/avv/accept`.
  avv: {
    accepted: boolean;
    acceptedAt: string | null;
    version: string | null;
  };
};

// The chrome-less first-run fork (router/index.ts). The only route on which the
// demo can go live under the user's feet — the fork's preparing screen polls the
// seed to completion.
const FORK_PATH = '/onboarding';
// The accept page of a mailed invitation. It is the page a new member is on while
// the sign-in navigates on to the workspace root, and the router guard's first
// status read (which wears the default persona) runs BEFORE that navigation has
// committed. Its URL carries the one-time token in the fragment, so reloading it
// meets a spent invitation ("no longer valid") instead of the demo the person
// just joined. Like the fork, it is never the page to reload. `/i` is the same
// page under the short path an SMS invitation links to (issue 698).
const INVITE_ACCEPT_PATHS = new Set(['/invite/accept', '/i']);

// Wearing a (new) persona is a whole-page decision: every view holds data
// fetched as the previous identity, so the shared state stores the choice and
// reloads (useDemoPersonaState). The DEFAULT persona is put on by whichever
// status fetch first sees the demo live — right after "explore the demo" that is
// the fork's own poll, and reloading the fork in place returns to /onboarding,
// where a live demo renders the exit gate, beating the fork's "demo live →
// workspace" navigation (#409 dev finding F1). Nothing on the fork was fetched as
// anybody, so the honest page to load with the mask on is the workspace root —
// and so it is from the invitation's accept page, whose token is spent by then.
//
// From the fork that is a navigation, not a page load (issue 806): with nothing to
// re-fetch, a full load only painted the boot frame between the preparing screen
// and the workspace — without the stage bar, which it cannot measure on a page
// that had none — and the bar then pushed the whole shell down. The router is
// imported lazily: it imports this store.
function reloadWearingPersona(): void {
  const { pathname } = window.location;
  if (pathname === FORK_PATH) void import('../router').then(({ default: router }) => router.replace('/'));
  else if (INVITE_ACCEPT_PATHS.has(pathname)) window.location.assign('/');
  else window.location.reload();
}

export const useOnboardingStore = defineStore('onboarding', () => {
  const status = ref<OnboardingStatus | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const lastFetchedAt = ref<number | null>(null);

  const mode = computed<OnboardingMode>(() => status.value?.mode ?? 'unset');
  const isAdmin = computed(() => status.value?.isAdmin === true);
  const isDemo = computed(() => status.value?.demo?.active === true);
  const isGuest = computed(() => status.value?.demo?.guest === true && !isAdmin.value);
  // The stage (#409): the persona sheet, trusted only while the demo is active,
  // and the persona worn — the same rules the module UIs apply via app-kit's
  // shared store, so one choice reads the same in every app on this origin.
  const stage = computed<DemoStage | null>(() =>
    isDemo.value && status.value?.demo?.stage ? status.value.demo.stage : null,
  );
  const persona = createDemoPersonaState({
    getTenantSlug: () => useTenantsStore().activeTenant?.slug,
    getUserId: () => useAuthStore().user?.userId,
    stage,
    reload: reloadWearingPersona,
  });
  const { activePersona, isMasked, setPersona } = persona;
  // The demo's module set while it is live, and what the fork and a reset may
  // choose from (ADR 0083) — the same reading app-kit's shared store gives.
  const demoModules = computed<string[] | null>(() => (isDemo.value ? status.value?.demo?.modules ?? null : null));
  const demoModuleOffer = computed<DemoOfferModule[]>(() => status.value?.demoOffer?.modules ?? []);
  const avvAccepted = computed(() => status.value?.avv?.accepted === true);
  // Is there staged wizard work that a re-asked fork could destroy? (ADR 0032
  // amendment, 2026-09-01.) This is the whole of the fork re-ask rule: "the fork
  // is re-asked exactly when there is no staged work to lose."
  //
  // The DECISION is read off the server, not remembered here: `staged` is the
  // projection of the master row's `stagedConfigJson`, which is the single record
  // of the guided choice and survives logout, another browser and another admin
  // (ADR 0032 Decision 1). A client-side "they picked guided" flag would be a
  // second, weaker memory of the same fact — it would forget on reload and re-ask
  // someone mid-answer, which is precisely what the amendment forbids.
  //
  // user-api already strips the reserved keys (`__applied`, `__commit`) from
  // `staged`, so replay markers can never be mistaken for staged answers. It is
  // also admin-only, which is fine: only admins reach the fork or the wizard.
  const hasStagedWork = computed(() => Object.keys(status.value?.staged ?? {}).length > 0);
  // Setup has begun (wizard opened + staged, or a commit is running) — the demo
  // banner switches its CTA to "continue" (issue 214).
  const setupStarted = computed(() => {
    const m = status.value?.mode;
    if (m === 'setup' || m === 'wiping' || m === 'applying') return true;
    return hasStagedWork.value;
  });
  const isSeeding = computed(() => status.value?.mode === 'demo_seeding');
  const isApplying = computed(() => status.value?.mode === 'applying');
  const firstRunResolved = computed(() => status.value?.firstRunResolved === true);
  const companyName = computed(() => status.value?.companyName ?? '');
  const checkedOutModules = computed<string[]>(() => status.value?.checkedOutModules ?? []);
  const sections = computed<Record<string, OnboardingSection>>(() => status.value?.sections ?? {});
  const staged = computed<Record<string, any>>(() => status.value?.staged ?? {});
  const applied = computed<Record<string, string>>(() => status.value?.applied ?? {});

  // The job-ad logo File is held CLIENT-SIDE only until commit replay (ADR 0032):
  // it is not part of the staged blob, so it is lost on reload/crash → the ATS
  // section shows "not yet uploaded, re-pick" and replay skips it (it is optional).
  const stagedLogoFile = ref<File | null>(null);

  // The suite record is tenant-scoped; every endpoint keys on the active tenant.
  // Resolve it the same way the rest of workspace-ui does — the shared tenants
  // store (host subdomain on prod, first membership on the bare/dev host).
  async function activeTenantId(): Promise<string | null> {
    const tenants = useTenantsStore();
    if (!tenants.loaded) await tenants.load().catch(() => {});
    return tenants.activeTenantId;
  }

  // Exposed (with `activeTenantId` and `apply`) because the onboarding WRITES no
  // longer live in this store (issue 195, D5). Each of them is a user-initiated
  // action on a specific screen — the fork's three buttons, a wizard section's
  // save, the commit screen's replay — and the seam that owns its pending state
  // and its failure has to be created in that component's setup. What stays here
  // is the shared status and the self-managing poll it drives.
  function base(tenantId: string) {
    return `/tenants/${tenantId}/onboarding`;
  }

  // Self-managing poll: while a seed OR wipe is in flight, re-fetch every 2.5s
  // until the mode settles (seed → demo on success / unset on failure; wipe →
  // setup on completion). Callers never manage the timer; it starts/stops off the
  // fetched mode.
  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  function clearPoll() {
    if (pollTimer) {
      clearTimeout(pollTimer);
      pollTimer = null;
    }
  }
  // Always clears then re-arms from the CURRENT mode, so it re-schedules the next
  // tick even after a transient fetch failure. The fired timer nulls itself before
  // running so its id is never left dangling — a single failed poll used to leave
  // pollTimer holding an already-fired id, which `if (!pollTimer)` then refused to
  // replace, permanently killing the poll (finding 2).
  function reconcilePoll() {
    clearPoll();
    const m = status.value?.mode;
    // Poll while a seed/commit is in flight so the fork/commit screen (and any
    // observing admin) tracks the transition. 'applying' is client-driven replay,
    // but polling still surfaces applied-marker progress + the stalled verdict.
    if (m === 'demo_seeding' || m === 'wiping' || m === 'applying') {
      pollTimer = setTimeout(() => {
        pollTimer = null;
        void fetchStatus(true);
      }, 2500);
    }
  }

  /** Adopt a status returned by one of the onboarding writes. */
  function apply(res: OnboardingStatus): OnboardingStatus {
    status.value = res;
    lastFetchedAt.value = Date.now();
    persona.applyDefaultPersona();
    reconcilePoll();
    return res;
  }

  // Cached for 2.5s — the router guard calls this on every navigation. Pass
  // force=true from the preparing screen / poll to bypass the cache.
  async function fetchStatus(force = false): Promise<OnboardingStatus | null> {
    if (loading.value) return status.value;
    if (!force && lastFetchedAt.value && Date.now() - lastFetchedAt.value < 2500) {
      return status.value;
    }
    const tenantId = await activeTenantId();
    if (!tenantId) return status.value;
    loading.value = true;
    error.value = null;
    try {
      const res = await userApi.get(`${base(tenantId)}/status`, { suppressGlobalError: true });
      status.value = res.data as OnboardingStatus;
      lastFetchedAt.value = Date.now();
      persona.applyDefaultPersona();
      return status.value;
    } catch (e: any) {
      // 403 TENANT_MEMBERSHIP_DENIED: a revoked demo guest / an ended demo — the app
      // root swaps to the ended page and this browser's state for it is dropped.
      noteTenantUnavailability(e, useTenantsStore().activeTenant?.slug, undefined, 'user-api');
      error.value = apiErrorMessage(e);
      return status.value;
    } finally {
      loading.value = false;
      // Re-arm (or stop) the poll off the freshest mode — in `finally` so a
      // transient fetch failure still schedules the next tick instead of killing
      // the poll (finding 2).
      reconcilePoll();
    }
  }

  // The journeys (#409 M4): the bar's task list per persona with this user's
  // progress. A failed read keeps the last answer (a module briefly down never
  // flips a tick off the screen); outside the demo there is nothing to show.
  const journeys = ref<DemoJourneys | null>(null);
  let journeysLoading = false;
  async function fetchJourneys(): Promise<DemoJourneys | null> {
    if (!isDemo.value) {
      journeys.value = null;
      return null;
    }
    if (journeysLoading) return journeys.value;
    journeysLoading = true;
    try {
      const tenantId = await activeTenantId();
      if (!tenantId) return journeys.value;
      const res = await userApi.get(`${base(tenantId)}/journeys`, { suppressGlobalError: true });
      journeys.value = res.data as DemoJourneys;
    } catch (e) {
      // Last known state stands — unless this person is no longer a member.
      noteTenantUnavailability(e, useTenantsStore().activeTenant?.slug, undefined, 'user-api');
    } finally {
      journeysLoading = false;
    }
    return journeys.value;
  }
  // The celebration write lives at the surface (AppChrome's seam call, issue
  // 195 D5); the store only marks the local copy so the line never shows twice
  // in one tab.
  function markCelebrated(personId: string): void {
    const entry = journeys.value?.journeys.find((j) => j.personId === personId);
    if (entry) entry.celebrated = true;
  }

  return {
    status,
    loading,
    error,
    mode,
    isAdmin,
    isDemo,
    avvAccepted,
    setupStarted,
    hasStagedWork,
    isSeeding,
    isApplying,
    firstRunResolved,
    companyName,
    isGuest,
    stage,
    demoModules,
    demoModuleOffer,
    activePersona,
    isMasked,
    setPersona,
    journeys,
    fetchJourneys,
    markCelebrated,
    checkedOutModules,
    sections,
    staged,
    applied,
    stagedLogoFile,
    fetchStatus,
    activeTenantId,
    base,
    apply,
  };
});
